import { supabase } from "../../../auth/supabaseClient";

const BRAND_BUCKET = "organization-assets";
const BRAND_TABLE = "platform_brand_assets";
const BRAND_CACHE_KEY = "oikos.platformBrandAssets.v1";

export const PLATFORM_BRAND_KEYS = {
  workplace: "edu.workplace",
  globalAdmin: "edu.workplace.global-admin",
  organizationAdmin: "edu.workplace.organization-admin",
  organizationAdminNavHome: "edu.workplace.organization-admin.nav.home",
  organizationAdminNavOrganization: "edu.workplace.organization-admin.nav.organization",
  organizationAdminNavDirectory: "edu.workplace.organization-admin.nav.directory",
  organizationAdminNavUsers: "edu.workplace.organization-admin.nav.users",
  organizationAdminNavGroups: "edu.workplace.organization-admin.nav.groups",
  organizationAdminNavUnits: "edu.workplace.organization-admin.nav.units",
  pages: "edu.workplace.pages",
  tables: "edu.workplace.tables",
  slides: "edu.workplace.slides",
  mail: "edu.workplace.mail",
  classroom: "edu.workplace.classroom",
  hallPass: "edu.workplace.hall-pass",
  teacherBoard: "edu.workplace.teacher-board",
  control: "edu.control",
  managedDevices: "edu.control.managed-devices",
  studentDockHome: "edu.control.student-dock.home",
  studentDockStore: "edu.control.student-dock.store",
  studentDockTesting: "edu.control.student-dock.testing",
};

export const EDU_SYSTEM_APP_BRAND_KEYS = {
  "global-admin": PLATFORM_BRAND_KEYS.globalAdmin,
  globaladmin: PLATFORM_BRAND_KEYS.globalAdmin,
  "oikos-global-admin": PLATFORM_BRAND_KEYS.globalAdmin,
  "org-admin": PLATFORM_BRAND_KEYS.organizationAdmin,
  "organization-admin": PLATFORM_BRAND_KEYS.organizationAdmin,
  "oikos-organization-admin": PLATFORM_BRAND_KEYS.organizationAdmin,
  "oikos-pages": PLATFORM_BRAND_KEYS.pages,
  "oikos-mail": PLATFORM_BRAND_KEYS.mail,
  "oikos-classroom": PLATFORM_BRAND_KEYS.classroom,
  "hall-pass": PLATFORM_BRAND_KEYS.hallPass,
  teacherboard: PLATFORM_BRAND_KEYS.teacherBoard,
  "teacher-board": PLATFORM_BRAND_KEYS.teacherBoard,
  "oikos-teacherboard": PLATFORM_BRAND_KEYS.teacherBoard,
  "oikos-tables": PLATFORM_BRAND_KEYS.tables,
  "oikos-slides": PLATFORM_BRAND_KEYS.slides,
};

function normalizeBrandAsset(row = {}) {
  return {
    key: String(row.asset_key || row.key || "").trim(),
    label: String(row.label || "").trim(),
    logoUrl: String(row.logo_url || row.logoUrl || "").trim(),
    iconUrl: String(row.icon_url || row.iconUrl || "").trim(),
    navIconUrl: String(row.nav_icon_url || row.navIconUrl || "").trim(),
    loginBackgroundUrl: String(row.login_background_url || row.loginBackgroundUrl || "").trim(),
    brandColor: String(row.brand_color || row.brandColor || "").trim(),
    updatedAt: row.updated_at || row.updatedAt || null,
  };
}

function appendBrandImageVersion(url = "", version = "") {
  if (!url || !version) return url || "";
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}v=${encodeURIComponent(version)}`;
}

export function getPlatformBrandImage(assets = {}, key = "", imageKind = "icon", fallback = "") {
  const asset = assets?.[key] || {};
  const version = asset.updatedAt || "";
  let imageUrl = "";
  if (imageKind === "logo") {
    imageUrl = asset.logoUrl || fallback || "";
  } else {
    imageUrl = asset.iconUrl || fallback || "";
  }
  return appendBrandImageVersion(imageUrl, version);
}

export function getPlatformBrandIconOnly(assets = {}, key = "", fallback = "") {
  const asset = assets?.[key] || {};
  return appendBrandImageVersion(asset.iconUrl || fallback || "", asset.updatedAt || "");
}

export function getPlatformBrandNavIconOnly(assets = {}, key = "", fallback = "") {
  const asset = assets?.[key] || {};
  return appendBrandImageVersion(asset.navIconUrl || fallback || "", asset.updatedAt || "");
}

export function getPlatformBrandLoginBackground(assets = {}, key = "", fallback = "") {
  const asset = assets?.[key] || {};
  return appendBrandImageVersion(asset.loginBackgroundUrl || fallback || "", asset.updatedAt || "");
}

export function getEduSystemAppBrandImage(assets = {}, appKey = "", fallback = "") {
  const brandKey = EDU_SYSTEM_APP_BRAND_KEYS[String(appKey || "").trim().toLowerCase()];
  return brandKey ? getPlatformBrandImage(assets, brandKey, "icon", fallback) : fallback || "";
}

export function getEduSystemAppBrandIcon(assets = {}, appKey = "", fallback = "") {
  const brandKey = EDU_SYSTEM_APP_BRAND_KEYS[String(appKey || "").trim().toLowerCase()];
  return brandKey ? getPlatformBrandIconOnly(assets, brandKey, fallback) : fallback || "";
}

export function getPlatformBrandColor(assets = {}, key = "", fallback = "") {
  return assets?.[key]?.brandColor || fallback || "";
}

export function getEduSystemAppBrandColor(assets = {}, appKey = "", fallback = "") {
  const brandKey = EDU_SYSTEM_APP_BRAND_KEYS[String(appKey || "").trim().toLowerCase()];
  return brandKey ? getPlatformBrandColor(assets, brandKey, fallback) : fallback || "";
}

export function getCachedPlatformBrandAssets() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(BRAND_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_error) {
    return {};
  }
}

function cachePlatformBrandAssets(assets = {}) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(assets || {}));
  } catch (_error) {
    // Cache misses should never block live branding.
  }
}

export async function fetchPlatformBrandAssets() {
  const { data, error } = await supabase
    .from(BRAND_TABLE)
    .select("asset_key,label,logo_url,icon_url,nav_icon_url,login_background_url,brand_color,updated_at")
    .in("asset_key", Object.values(PLATFORM_BRAND_KEYS));

  if (error) {
    console.warn("Platform brand assets unavailable:", error);
    return {};
  }

  const assets = (data || []).reduce((assets, row) => {
    const asset = normalizeBrandAsset(row);
    if (asset.key) {
      assets[asset.key] = asset;
    }
    return assets;
  }, {});
  cachePlatformBrandAssets(assets);
  return assets;
}

export async function savePlatformBrandAsset(asset = {}) {
  const payload = normalizeBrandAsset(asset);
  if (!payload.key) {
    throw new Error("Missing platform asset key.");
  }

  const { data, error } = await supabase
    .from(BRAND_TABLE)
    .upsert({
      asset_key: payload.key,
      label: payload.label,
      logo_url: payload.logoUrl,
      icon_url: payload.iconUrl,
      nav_icon_url: payload.navIconUrl,
      login_background_url: payload.loginBackgroundUrl,
      brand_color: payload.brandColor,
    }, { onConflict: "asset_key" })
    .select("asset_key,label,logo_url,icon_url,nav_icon_url,login_background_url,brand_color,updated_at")
    .single();

  if (error) throw error;
  return normalizeBrandAsset(data);
}

export async function uploadPlatformBrandImage(assetKey, imageKind, file) {
  const cleanKey = String(assetKey || "").trim().toLowerCase().replace(/[^a-z0-9._-]/g, "-");
  const cleanKind = imageKind === "logo"
    ? "logo"
    : imageKind === "loginBackground"
      ? "login-background"
      : imageKind === "navIcon"
        ? "nav-icon"
        : "icon";

  if (!cleanKey) {
    throw new Error("Missing platform asset key.");
  }

  if (!file) {
    throw new Error("Choose an image to upload.");
  }

  if (!String(file.type || "").startsWith("image/")) {
    throw new Error("Upload must be an image file.");
  }

  const safeName = file.name
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9._-]/g, "")
    .toLowerCase();
  const filePath = `platform/brand-assets/${cleanKey}/${cleanKind}/${Date.now()}-${safeName || cleanKind}`;

  const { error: uploadError } = await supabase.storage
    .from(BRAND_BUCKET)
    .upload(filePath, file, {
      cacheControl: "3600",
      upsert: false,
    });

  if (uploadError) throw uploadError;

  const {
    data: { publicUrl },
  } = supabase.storage.from(BRAND_BUCKET).getPublicUrl(filePath);

  return publicUrl;
}
